import React from "react";
import { notNullOrUndefined } from "../../../util/functions/notNullOrUndefined";
import { Angle } from "./Angle";
import {
	CoordinateSpace,
	type ICoordinateSpace,
	type InFullSizeImage,
	type InMonitorSetup,
	type InOverviewImage,
	type InTransformed,
	type InVisibleCanvas,
	type InVisibleCanvasDrawRegion,
	type InWindow,
} from "./CoordinateSpace";
import type { ILine } from "./Line";
import type { IPoint } from "./Point";
import type { ITransformation } from "./Transformation";
import type { ITransformedImageRegionsStore } from "../viewports/viewport-series/image-regions/TransformedImageRegionsStore";
import type { IZoomStore } from "../viewports/viewport-series/zoom/ZoomStore";
import type { ICurrentImageTransformationStore } from "../viewports/viewport-series/transformations/CurrentImageTransformationStore";
import type { IVisibleCanvasDrawRegionStore } from "../viewports/viewport-series/visible-canvas/VisibleCanvasDrawRegionStore";

/**
 * Represents a movement from (0,0) -> (x,y) in an x,y coordinate space.
 */
export interface IVector<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>> {
	readonly type: "vector";
	readonly coordinateSpace: TCoordinateSpace;
	readonly kind: string;
	readonly x: number;
	readonly y: number;

	/**
	 * Adds two vectors to each other
	 * @param other
	 */
	add(other: IVector<TCoordinateSpace>): IVector<TCoordinateSpace>;

	/**
	 * Subtracts a vector from this vector
	 * @param other
	 */
	subtract(other: IVector<TCoordinateSpace>): IVector<TCoordinateSpace>;

	/**
	 * Multiplies the (x,y) values of this vector by the provided value
	 * @param value
	 */
	multiplyBy(value: number): IVector<TCoordinateSpace>;

	/**
	 * Divides the (x,y) values of this vector by the provided value
	 * @param value
	 */
	divideBy(value: number): IVector<TCoordinateSpace>;

	/**
	 * Calculates the "average" vector between this vector and the other vector
	 * @param other
	 */
	average(other: IVector<TCoordinateSpace>): IVector<TCoordinateSpace>;

	/**
	 * Returns a vector that moves in the exact opposite direction
	 */
	negate(): IVector<TCoordinateSpace>;

	/**
	 * Returns a vector that is perpendicular (a 90° angle) to this vector, by rotating this vector clockwise
	 */
	perpendicularClockwise(): IVector<TCoordinateSpace>;

	/**
	 * Returns a vector that is perpendicular (a 90° angle) to this vector, by rotating this vector counter clockwise
	 */
	perpendicularCounterClockwise(): IVector<TCoordinateSpace>;

	/**
	 * Returns a vector with the same direction but with the provided length
	 * @param length The new length
	 */
	resize(length: number): IVector<TCoordinateSpace>;

	/**
	 * transforms the current vector to a new vector in the transformed coordinate space.
	 */
	transform(transformation: ITransformation<TCoordinateSpace>): IVector<InTransformed<TCoordinateSpace>>;

	/**
	 * Returns the length of this vector
	 */
	length(): number;

	/**
	 * Returns a vector with the same x value but with y set to 0
	 */
	toHorizontal(): IVector<TCoordinateSpace>;

	/**
	 * Returns a vector with the same y value but with x set to 0
	 */
	toVertical(): IVector<TCoordinateSpace>;

	/**
	 * Returns the angle that this vector makes relative to the x axis
	 */
	horizontalAngle(): Angle;

	/**
	 * Returns the angle that this vector makes relative to the y axis
	 */
	verticalAngle(): Angle;

	/**
	 * Draws a line from the given point along this vector
	 */
	toLine(from: IPoint<TCoordinateSpace>): ILine<TCoordinateSpace>;

	equals(other: IVector<TCoordinateSpace>): boolean;

	/**
	 * Rounds the parameters of the vectors, and then compares them
	 */
	equalsWithPrecision(other: IVector<TCoordinateSpace>, roundingDigits: number): boolean;

	toString(): string;
}

export abstract class Vector<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>> implements IVector<TCoordinateSpace> {
	public readonly type: "vector";

	abstract readonly kind: string;

	protected constructor(
		public readonly coordinateSpace: TCoordinateSpace,
		public readonly x: number,
		public readonly y: number,
	) {
		this.type = "vector";

		if (x === null || x === undefined) throw new Error("X is null or undefined");
		if (y === null || y === undefined) throw new Error("Y is null or undefined");
		if (isNaN(x)) throw new Error("X is NaN");
		if (isNaN(y)) throw new Error("Y is NaN");
	}

	add(other: IVector<TCoordinateSpace>): IVector<TCoordinateSpace> {
		return this.coordinateSpace.createVector(this.x + other.x, this.y + other.y);
	}

	subtract(other: IVector<TCoordinateSpace>): IVector<TCoordinateSpace> {
		return this.coordinateSpace.createVector(this.x - other.x, this.y - other.y);
	}

	multiplyBy(value: number): IVector<TCoordinateSpace> {
		return this.coordinateSpace.createVector(this.x * value, this.y * value);
	}

	divideBy(value: number): IVector<TCoordinateSpace> {
		return this.coordinateSpace.createVector(this.x / value, this.y / value);
	}

	average(other: IVector<TCoordinateSpace>): IVector<TCoordinateSpace> {
		const { x: x1, y: y1 } = this;
		const { x: x2, y: y2 } = other;
		return this.coordinateSpace.createVector(x1 + (x2 - x1) / 2, y1 + (y2 - y1) / 2);
	}

	negate(): IVector<TCoordinateSpace> {
		return this.multiplyBy(-1);
	}

	perpendicularClockwise() {
		const { x, y } = this;
		// noinspection JSSuspiciousNameCombination We explicitly want to swap these fields
		return this.coordinateSpace.createVector(y, -x);
	}

	perpendicularCounterClockwise() {
		const { x, y } = this;
		// noinspection JSSuspiciousNameCombination We explicitly want to swap these fields
		return this.coordinateSpace.createVector(-y, x);
	}

	resize(length: number) {
		const currentLength = this.length();
		if (currentLength === 0) {
			console.warn("📏 Cannot resize a vector with length 0");
			return this;
		}
		return this.multiplyBy(length / currentLength);
	}

	transform(transformation: ITransformation<TCoordinateSpace>): IVector<InTransformed<TCoordinateSpace>> {
		const { x, y } = this;

		return new VectorInTransformed(this.coordinateSpace, transformation, x, y);
	}

	toHorizontal() {
		return this.coordinateSpace.createVector(this.x, 0);
	}

	toVertical() {
		return this.coordinateSpace.createVector(0, this.y);
	}

	horizontalAngle() {
		const { x, y } = this;
		return Angle.fromRadians(Math.atan2(y, x));
	}

	verticalAngle() {
		const { x, y } = this;
		return Angle.fromRadians(Math.PI / 2 - Math.atan2(y, x));
	}

	toLine(from: IPoint<TCoordinateSpace>) {
		return this.coordinateSpace.createLine(from, from.move(this));
	}

	length(): number {
		const { x, y } = this;
		return Math.hypot(x, y);
	}

	equals(other: IVector<TCoordinateSpace>) {
		return this.kind === other.kind && this.x === other.x && this.y === other.y;
	}

	equalsWithPrecision(other: IVector<TCoordinateSpace>, numberOfPrecisionDigits: number) {
		return (
			this.kind === other.kind &&
			Math.floor(this.x * Math.pow(10, numberOfPrecisionDigits)) === Math.floor(other.x * Math.pow(10, numberOfPrecisionDigits)) &&
			Math.floor(this.y * Math.pow(10, numberOfPrecisionDigits)) === Math.floor(other.y * Math.pow(10, numberOfPrecisionDigits))
		);
	}

	toString() {
		const { x, y } = this;
		return `(${x.toFixed(2)}, ${y.toFixed(2)})`;
	}
}

export interface IVectorInWindow extends IVector<InWindow> {}

export interface IVectorInMonitorSetup extends IVector<InMonitorSetup> {}

export interface IVectorInVisibleCanvas extends IVector<InVisibleCanvas> {}

export interface IVectorInVisibleCanvasDrawRegion extends IVector<InVisibleCanvasDrawRegion> {}

export interface IVectorInOverviewImage extends IVector<InOverviewImage> {}

export interface IVectorInFullSizeImage extends IVector<InFullSizeImage> {}

export interface IVectorInTransformedFullSizeImage extends IVector<InTransformed<InFullSizeImage>> {}

/**
 * A Vector in the coordinate space of the current monitor setup
 */
export class VectorInMonitorSetup extends Vector<InMonitorSetup> implements IVectorInMonitorSetup {
	readonly kind = "vector-in-monitor-setup" as const;

	constructor(x: number, y: number) {
		super(CoordinateSpace.inMonitorSetup, x, y);
	}
}

/**
 * A Vector in the coordinate space of the current HTML window
 */
export class VectorInWindow extends Vector<InWindow> implements IVectorInWindow {
	readonly kind = "vector-in-window" as const;

	constructor(x: number, y: number) {
		super(CoordinateSpace.inWindow, x, y);
	}

	static fromPointerEvent(e: PointerEvent | React.PointerEvent): IVectorInWindow | null {
		return notNullOrUndefined(e.movementX) && notNullOrUndefined(e.movementY) && (e.movementX !== 0 || e.movementY !== 0) ? new VectorInWindow(e.movementX, e.movementY) : null;
	}
}

export const vectorInWindowToVectorInTransformedFullSizeImage =
	(zoomStore: IZoomStore, currentImageTransformationStore: ICurrentImageTransformationStore) => (vector: IVector<InWindow>) => {
		const { currentZoomFactor } = zoomStore;
		const { imageTransformation } = currentImageTransformationStore;
		const { x, y } = vector;
		return new VectorInTransformedFullSizeImage(imageTransformation, x / currentZoomFactor!.value, y / currentZoomFactor!.value);
	};

/**
 * A Vector in the coordinate space of a visible canvas
 * (0,0) denotes the top left pixel of the visible canvas
 */
export class VectorInVisibleCanvas extends Vector<InVisibleCanvas> implements IVectorInVisibleCanvas {
	readonly kind = "vector-in-visible-canvas" as const;

	constructor(x: number, y: number) {
		super(CoordinateSpace.inVisibleCanvas, x, y);
	}
}

/**
 * A Vector in the coordinate space of the draw region of a visible canvas.
 * (0,0) denotes the top left pixel of the draw region of the visible canvas
 */
export class VectorInVisibleCanvasDrawRegion extends Vector<InVisibleCanvasDrawRegion> implements IVectorInVisibleCanvasDrawRegion {
	readonly kind = "vector-in-visible-canvas-draw-region" as const;

	constructor(x: number, y: number) {
		super(CoordinateSpace.inVisibleCanvasDrawRegion, x, y);
	}
}

export const vectorInVisibleCanvasDrawRegionToVectorInTransformedFullImage =
	(transformedImageRegionsStore: ITransformedImageRegionsStore, visibleCanvasDrawRegionStore: IVisibleCanvasDrawRegionStore) => (vector: IVector<InVisibleCanvasDrawRegion>) => {
		const { currentTransformedFullSizeImageRegion, currentTransformedFullSizeImageRegionTransformation } = transformedImageRegionsStore;
		const { visibleCanvasDrawRegion } = visibleCanvasDrawRegionStore;

		if (!visibleCanvasDrawRegion) throw new Error("Cannot convert this vector to a vector in transformed full size image region because there is no visible canvas draw region");
		if (!currentTransformedFullSizeImageRegion)
			throw new Error("Cannot convert this vector to a vector in transformed full size image region because there is no transformed full size image region");

		const currentTransformedFullSizeImageRegionSize = currentTransformedFullSizeImageRegion.size;
		const visibleCanvasDrawRegionSize = visibleCanvasDrawRegion.size;

		const ratio = currentTransformedFullSizeImageRegionSize.width / visibleCanvasDrawRegionSize.width;

		return new VectorInTransformed<InFullSizeImage>(
			CoordinateSpace.inFullSizeImage,
			currentTransformedFullSizeImageRegionTransformation,
			Math.round(ratio * vector.x + currentTransformedFullSizeImageRegion.topLeft.x),
			Math.round(ratio * vector.y + currentTransformedFullSizeImageRegion.topLeft.y),
		);
	};

/**
 * A Vector in the coordinate space of the overview image
 */
export class VectorInOverviewImage extends Vector<InOverviewImage> implements IVectorInOverviewImage {
	readonly kind = "vector-in-overview-image" as const;

	constructor(x: number, y: number) {
		super(CoordinateSpace.inOverviewImage, x, y);
	}
}

/**
 * A Vector in the coordinate space of the full size image
 */
export class VectorInFullSizeImage extends Vector<InFullSizeImage> implements IVectorInFullSizeImage {
	readonly kind = "vector-in-full-size-image" as const;

	constructor(x: number, y: number) {
		super(CoordinateSpace.inFullSizeImage, x, y);
	}
}

export const vectorInFullImageToVectorInWindow = (zoomStore: IZoomStore) => (vector: IVector<InFullSizeImage>) => {
	const { currentZoomFactor } = zoomStore;
	const { x, y } = vector;
	return new VectorInWindow(x * currentZoomFactor!.value, y * currentZoomFactor!.value);
};

/**
 * A Vector in the coordinate space of the transformed full size image
 */
export class VectorInTransformed<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>> extends Vector<InTransformed<TCoordinateSpace>> {
	readonly kind = "vector-in-transformed" as const;

	constructor(originalCoordinateSpace: ICoordinateSpace<TCoordinateSpace>, transformation: ITransformation<TCoordinateSpace>, x: number, y: number) {
		super(CoordinateSpace.inTransformed(originalCoordinateSpace, transformation), x, y);
	}
}

export const transformVectorToOriginal =
	<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>>(coordinateSpace: InTransformed<TCoordinateSpace>) =>
	(vector: IVector<InTransformed<TCoordinateSpace>>) => {
		const { x, y } = vector;
		const { originalCoordinateSpace } = coordinateSpace;

		//vectors don't need to transform as the x/y axis is always oriented the same way as the inWindow or inFullSizeImage x/y axises
		return originalCoordinateSpace.createVector(x, y);
	};

export class VectorInTransformedFullSizeImage extends VectorInTransformed<InFullSizeImage> implements IVectorInTransformedFullSizeImage {
	constructor(transformation: ITransformation<InFullSizeImage>, x: number, y: number) {
		super(CoordinateSpace.inFullSizeImage, transformation, x, y);
	}
}

export function isVectorInMonitorSetup(vector: IVector<any>): vector is IVector<InMonitorSetup> {
	return vector.coordinateSpace.kind === CoordinateSpace.inMonitorSetup.kind;
}

export function isVectorInWindow(vector: IVector<any>): vector is IVector<InWindow> {
	return vector.coordinateSpace.kind === CoordinateSpace.inWindow.kind;
}

export function isVectorInVisibleCanvas(vector: IVector<any>): vector is IVector<InVisibleCanvas> {
	return vector.coordinateSpace.kind === CoordinateSpace.inVisibleCanvas.kind;
}

export function isVectorInVisibleCanvasDrawRegion(vector: IVector<any>): vector is IVector<InVisibleCanvasDrawRegion> {
	return vector.coordinateSpace.kind === CoordinateSpace.inVisibleCanvasDrawRegion.kind;
}

export function isVectorInOverviewImage(vector: IVector<any>): vector is IVector<InOverviewImage> {
	return vector.coordinateSpace.kind === CoordinateSpace.inOverviewImage.kind;
}

export function isVectorInFullSizeImage(vector: IVector<any>): vector is IVector<InFullSizeImage> {
	return vector.coordinateSpace.kind === CoordinateSpace.inFullSizeImage.kind;
}

export function isVectorInTransformed(vector: IVector<any>): vector is IVector<InTransformed<InFullSizeImage>> {
	return vector.coordinateSpace.kind === "in-transformed";
}
